/**
 * useMediaCmsPayload
 * ------------------
 * Fetches `/api/media?lang=<lang>` once per language and caches the
 * result at module scope for the lifetime of the tab. Designed to feed
 * `useMediaContent()` with a normalized CMS payload while preserving
 * the ability to fall back silently to hardcoded data.
 *
 * Contract
 *   Returns `{ payload, source, error, loading }` where:
 *     payload  – the raw `/api/media` object, or `null` if unavailable.
 *     source   – "cms"      → CMS payload passed the healthy check
 *                "fallback" → any failure mode (501, timeout, 5xx,
 *                             malformed shape) — caller uses hardcoded
 *                "loading"  → the first render before the request has
 *                             resolved; caller should still render the
 *                             hardcoded fallback so the page never
 *                             blanks. Once resolved, `source` flips to
 *                             either "cms" or "fallback".
 *     error    – short human-readable label of the failure (or null)
 *     loading  – true only during the initial in-flight fetch
 *
 * Guarantees
 *   • One /api/media request per (lang) per tab. React StrictMode's
 *     double-invocation of effects will not double-fetch because the
 *     module-level cache short-circuits.
 *   • Explicit 5s timeout via AbortController — a slow CMS API can
 *     never hold the page hostage. Timeout counts as "fallback".
 *   • Retries once on transient network errors (excluding 501, which
 *     is the CMS-rollback sentinel).
 *   • No polling. Data is fetched on mount and on `lang` change.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// --- Config ---------------------------------------------------------------
const REQUEST_TIMEOUT_MS = 5000;
const RETRIES_ON_TRANSIENT = 1;

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND.replace(/\/$/, "")}/api`;

// --- Module cache ---------------------------------------------------------
// Map<lang, {payload, source, error, at}>. Survives component remounts,
// resets on hard page reload as expected.
const cache = new Map();
// Map<lang, Promise>. Coalesces concurrent fetches during React's
// StrictMode double-invocation and rapid EN↔AR toggling.
const inflight = new Map();

/** Force-clear the cache — surfaced for tests + admin cache-bust flows. */
export function clearMediaCmsCache() {
  cache.clear();
  inflight.clear();
}

// --- Fetch ---------------------------------------------------------------
async function fetchOnce(lang, signal) {
  const url = `${API_BASE}/media?lang=${encodeURIComponent(lang)}`;
  const res = await axios.get(url, {
    timeout: REQUEST_TIMEOUT_MS,
    signal,
    // Never send cookies/creds on the public payload — this endpoint
    // is deliberately public when CMS is enabled + rollback is off.
    withCredentials: false,
    validateStatus: () => true, // let us branch on status ourselves
  });
  return { status: res.status, data: res.data };
}

async function loadCmsPayload(lang) {
  // Return cache hit synchronously via the Promise contract.
  if (cache.has(lang)) return cache.get(lang);
  if (inflight.has(lang)) return inflight.get(lang);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS + 250);
  const task = (async () => {
    let lastErr = null;
    for (let attempt = 0; attempt <= RETRIES_ON_TRANSIENT; attempt++) {
      try {
        const { status, data } = await fetchOnce(lang, controller.signal);
        // 501 → CMS disabled or rollback engaged. Fallback silently.
        if (status === 501) {
          const entry = { payload: null, source: "fallback",
                          error: "cms_disabled", at: Date.now() };
          cache.set(lang, entry);
          return entry;
        }
        // 5xx → treat as transient; may retry once then fallback.
        if (status >= 500) {
          lastErr = `http_${status}`;
          if (attempt < RETRIES_ON_TRANSIENT) continue;
          const entry = { payload: null, source: "fallback",
                          error: lastErr, at: Date.now() };
          cache.set(lang, entry);
          return entry;
        }
        // Non-2xx → fallback, no retry.
        if (status < 200 || status >= 300) {
          const entry = { payload: null, source: "fallback",
                          error: `http_${status}`, at: Date.now() };
          cache.set(lang, entry);
          return entry;
        }
        // Malformed body → fallback.
        if (!data || typeof data !== "object" || Array.isArray(data)) {
          const entry = { payload: null, source: "fallback",
                          error: "malformed_body", at: Date.now() };
          cache.set(lang, entry);
          return entry;
        }
        const entry = { payload: data, source: "cms", error: null, at: Date.now() };
        cache.set(lang, entry);
        return entry;
      } catch (err) {
        // Abort → timeout. Do not retry.
        if (err?.name === "CanceledError" || err?.name === "AbortError"
            || err?.code === "ERR_CANCELED" || err?.code === "ECONNABORTED") {
          const entry = { payload: null, source: "fallback",
                          error: "timeout", at: Date.now() };
          cache.set(lang, entry);
          return entry;
        }
        lastErr = err?.message || "network_error";
        if (attempt < RETRIES_ON_TRANSIENT) continue;
        const entry = { payload: null, source: "fallback",
                        error: lastErr, at: Date.now() };
        cache.set(lang, entry);
        return entry;
      }
    }
    const entry = { payload: null, source: "fallback",
                    error: lastErr || "unknown", at: Date.now() };
    cache.set(lang, entry);
    return entry;
  })().finally(() => {
    clearTimeout(timer);
    inflight.delete(lang);
  });

  inflight.set(lang, task);
  return task;
}

// --- Hook ----------------------------------------------------------------
/**
 * React hook. Kicks off the fetch on mount and on language change.
 * The first render always returns `{payload:null, source:"loading"}`
 * (unless the payload is already cached from a previous mount).
 */
export function useMediaCmsPayload(lang) {
  // Try to seed from the cache synchronously so a warm mount (e.g. AR
  // → EN → AR again) never flashes "loading" back to fallback.
  const seed = cache.get(lang);
  const [state, setState] = useState(() => {
    if (seed) return seed;
    return { payload: null, source: "loading", error: null, at: 0 };
  });
  const activeLangRef = useRef(lang);

  useEffect(() => {
    activeLangRef.current = lang;
    let cancelled = false;
    // If we already have a cached result for this lang, sync state
    // (covers the case where cache was populated after mount by a
    // sibling component).
    const cached = cache.get(lang);
    if (cached) {
      setState(cached);
      return () => { cancelled = true; };
    }
    setState((s) => (s.source === "loading" ? s : { payload: null, source: "loading", error: null, at: 0 }));
    loadCmsPayload(lang).then((entry) => {
      if (cancelled) return;
      if (activeLangRef.current !== lang) return; // stale race
      setState(entry);
    });
    return () => { cancelled = true; };
  }, [lang]);

  return useMemo(() => ({
    payload: state.payload,
    source:  state.source,
    error:   state.error,
    loading: state.source === "loading",
  }), [state]);
}
